import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { DatePickerField, toUTCISODate } from "src/view/conponents/DateRangePicker";
import { AnimatedCollapse } from "@ui/panel/AnimatedCollapse";
import { Typo } from "@ui/text/Typo";
import Signal from "badmfck-signal";
import { CalendarDays, ChevronDown, Lock, Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import { notify } from "src/tools/notify";
import { AdminButton } from "../pages/administration/AdminControls";
import type { TFeesTargetType, TFeesType } from "../Fees";
import { FormParser } from "../FormParser";
import { S_CONFIRM } from "../popup/general/ConfirmDialog";
import { feeStates, formatDate, type TFeeState } from "./providersData";
import { Card, DialogBody, embeddedFormSx, EmptyState, IconButton, Notice, Pill, ProviderDialog, Section, SkeletonRows } from "./providersUi";

const TEXT: Record<TFeesType, { title: string; one: string; empty: string; hint: string }> = {
	rates: { get title() { return msg("rates"); }, get one() { return msg("rateConfiguration"); }, get empty() { return msg("noRatesYet"); }, get hint() { return msg("commissionsChargedForPaymentsThatGoThroughThisChannel"); } },
	limits: { get title() { return msg("limits"); }, get one() { return msg("limitsConfiguration"); }, get empty() { return msg("noLimitsYet"); }, get hint() { return msg("amountAndVolumeLimitsAPaymentHasToFitInto"); } },
	settings: { get title() { return msg("settings"); }, get one() { return msg("settingsConfiguration"); }, get empty() { return msg("noSettingsYet"); }, get hint() { return msg("allowedCountriesAndOtherProcessingSettings"); } },
	rollings: { get title() { return msg("rollingReserve"); }, get one() { return msg("rollingConfiguration"); }, get empty() { return msg("noRollingReserveYet"); }, get hint() { return msg("shareOfTurnoverHeldBackAsAReserve"); } },
	setup: { get title() { return msg("setupFees"); }, get one() { return msg("setupConfiguration"); }, get empty() { return msg("noSetupFeesYet"); }, get hint() { return msg("oneOffAndRecurringServiceFees"); } },
};

const STATE: Record<TFeeState, { label: string; tone: "positive" | "neutral" | "info" }> = {
	current: { get label() { return msg("inEffect"); }, tone: "positive" },
	scheduled: { get label() { return msg("scheduled"); }, tone: "info" },
	archived: { get label() { return msg("archived"); }, tone: "neutral" },
};

const showError = (error: IError) =>
	notify.error(() => (
		<div>
			<Typo variant="text">{error.message}</Typo>
			{error.details && <Typo variant="medium">{error.details}</Typo>}
		</div>
	));

const Timeline = styled.div`
	position: relative;
	.fee {
		position: relative;
		padding-left: 26px;
	}
	.fee + .fee {
		margin-top: 14px;
	}
	/* One line runs through the markers, so the configurations read as a history. */
	.fee::before {
		content: "";
		position: absolute;
		top: 26px;
		bottom: -14px;
		left: 6px;
		width: 2px;
		background: #e4e7ec;
	}
	.fee:last-child::before {
		display: none;
	}
	.marker {
		position: absolute;
		top: 16px;
		left: 0;
		width: 14px;
		height: 14px;
		border: 3px solid #fff;
		border-radius: 50%;
		background: #98a2b3;
		box-shadow: 0 0 0 1px #d0d5dd;
	}
	.fee.current .marker {
		background: #17b26a;
		box-shadow: 0 0 0 1px #abefc6;
	}
	.fee.scheduled .marker {
		background: #2e90fa;
		box-shadow: 0 0 0 1px #b2ddff;
	}
	.bar {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px 12px;
		min-height: 46px;
		padding: 7px 10px 7px 16px;
		border: 1px solid #d0d5dd;
		border-radius: 10px;
		background: #fff;
		box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
		cursor: pointer;
	}
	.bar:hover {
		background: #fcfcfd;
	}
	.bar:focus-visible {
		outline: 2px solid #84adff;
		outline-offset: 2px;
	}
	.fee.archived .bar {
		box-shadow: none;
	}
	.when {
		flex: 1 1 auto;
		min-width: 0;
		color: #182230;
		font-weight: 600;
	}
	.when .from {
		color: #667085;
		font-weight: 400;
	}
	.when .currency {
		margin-left: 8px;
		color: #667085;
		font-size: 12px;
		font-weight: 500;
	}
	.lock {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		color: #98a2b3;
		font-size: 12px;
	}
	.tools {
		display: flex;
		align-items: center;
		gap: 2px;
		margin-left: auto;
	}
	.toggle {
		color: #98a2b3;
		transition: transform 0.18s;
	}
	.fee.open .toggle {
		transform: rotate(180deg);
	}
	.form {
		padding-top: 12px;
	}
`;

const Hint = styled.p`
	margin: -2px 0 14px;
	color: #667085;
	font-size: 12px;
`;

/** Fee configurations of one channel as a history: the one in effect, the scheduled ones and the archive. */
export const ModernFees = ({ target_id, type, target_type, label }: { target_id: number; type: TFeesType; target_type: TFeesTargetType; label?: string }) => {
	 useLocale();
	const [response, reload, busy] = useAPI<any[]>({ method: "fees/get", data: { target_id, type, target_type } });
	const text = TEXT[type];
	const fees = Array.isArray(response?.data) ? response.data : [];
	const states = useMemo(() => feeStates(fees), [fees]);
	// Only the configuration in effect starts open (or the nearest scheduled one), so the history stays readable.
	const opened = states.includes("current") ? states.indexOf("current") : states.lastIndexOf("scheduled");
	const loading = !response && busy;
	const add = () => S_ADD_FEES_MODERN.invoke({ target_id, type, target_type, label, onClose: () => reload() });
	const addButton = (primary?: boolean) => (
		<AdminButton variant={primary ? "primarySmall" : "secondarySmall"} size="small" disabled={busy} onClick={add}>
			<Plus size={14} />{msg("addConfiguration")}</AdminButton>
	);

	return (
		<div>
			<Section title={text.title} count={response ? fees.length : undefined} action={fees.length > 0 && addButton()} />
			<Hint>
				{text.hint}{msg("theLatestConfigurationWhoseDateHasComeAppliesAScheduledOneTa_efc34f3f")}</Hint>
			{response?.error && (
				<Notice tone="error" action={<AdminButton variant="secondarySmall" size="small" onClick={() => reload()}>{msg("retry")}</AdminButton>}>
					{response.error.message}
				</Notice>
			)}
			{loading && (
				<Card>
					<SkeletonRows rows={2} />
				</Card>
			)}
			{response && !response.error && fees.length === 0 && (
				<Card>
					<EmptyState
						icon={<SlidersHorizontal size={20} />}
						title={text.empty}
						text={msg("templateAddTheFirstForThisChannelYouChooseTheDateItStartsToApply_df8011e8", { v0: text.one })}
						action={addButton(true)}
					/>
				</Card>
			)}
			{fees.length > 0 && (
				<Timeline>
					{fees.map((fee: any, index: number) => (
						<FeeEntry key={fee.id + "_" + index} fee={fee} state={states[index]} initiallyOpen={index === opened} onChange={() => reload()} />
					))}
				</Timeline>
			)}
		</div>
	);
};

const FeeEntry = ({ fee, state, initiallyOpen, onChange }: { fee: any; state: TFeeState; initiallyOpen: boolean; onChange: () => void }) => {
	useLocale();
	const [open, setOpen] = useState(initiallyOpen);
	const [editingDate, setEditingDate] = useState(false);
	const date = formatDate(fee.activated_from) ?? "—";
	const canDelete = !fee.historical && !fee.locked;
	const meta = STATE[state];

	const changeDate = async (value: string) => {
		const response = await GD.REQ_API.request({ method: "fees/set", data: { ...fee, activated_from: value } });
		if (response.error) return showError(response.error);
		notify.success(msg("activationDateChanged"));
		onChange();
	};

	const remove = () =>
		S_CONFIRM.invoke({
			title: msg("deleteConfiguration"),
			message: msg("templateDeleteTheConfigurationThatStartsOnThisCannotBeUndone_473c99f6", { v0: date }),
			confirm: "Delete",
			onResult: async value => {
				if (value !== "confirm") return;
				const result = await GD.REQ_API.request({
					method: "fees/delete",
					data: { id: fee.id, target_id: fee.target_id, type: fee.type, target_type: fee.target_type },
				});
				if (result.error) return showError(result.error);
				notify.success(msg("configurationDeleted"));
				onChange();
			},
		});

	return (
		<div className={`fee ${state}${open ? " open" : ""}`}>
			<span className="marker" aria-hidden="true" />
			<div
				className="bar"
				role="button"
				tabIndex={0}
				aria-expanded={open}
				onClick={() => setOpen(!open)}
				onKeyDown={event => {
					if (event.target !== event.currentTarget || (event.key !== "Enter" && event.key !== " ")) return;
					event.preventDefault();
					setOpen(!open);
				}}>
				<Pill tone={meta.tone} dot>
					{meta.label}
				</Pill>
				<span className="when">
					<span className="from">{state === "scheduled" ? msg("starts") : msg("from_e484a95d")}</span>
					<DateTimeTooltip value={date === "—" ? undefined : fee.activated_from}>{date}</DateTimeTooltip>
					{fee.currency && <span className="currency">{fee.currency}</span>}
				</span>
				<span className="tools">
					{fee.locked ? (
						<span className="lock" title={msg("thisConfigurationHasAlreadyStartedSoItsDateIsFixed")}>
							<Lock size={13} aria-hidden="true" />
							{fee.editable ? msg("dateFixed") : msg("readOnly")}
						</span>
					) : (
						<IconButton label={msg("changeActivationDate")} tone="accent" onClick={() => setEditingDate(true)}>
							<CalendarDays size={16} />
						</IconButton>
					)}
					{canDelete && (
						<IconButton label={msg("deleteConfiguration")} tone="danger" onClick={remove}>
							<Trash2 size={16} />
						</IconButton>
					)}
					<ChevronDown className="toggle" size={18} aria-hidden="true" />
				</span>
			</div>
			<AnimatedCollapse expanded={open}>
				<div className="form">
					<FormParser
						appearance="modern"
						sx={embeddedFormSx}
						size="small"
						grow
						previewMode={!fee.editable}
						key={fee.type + fee.activated_from + fee.id}
						secondaryButton={null}
						onSubmit={async (d: any): Promise<any> => {
							fee.data = d;
							const response = await GD.REQ_API.request({ method: "fees/set", data: fee });
							if (response.error) return response.error;
							return fee;
						}}
						onRequest={async (): Promise<any> => fee}
					/>
				</div>
			</AnimatedCollapse>
			{editingDate && <ActivationDateDialog initial={fee.activated_from} onClose={() => setEditingDate(false)} onSave={value => { setEditingDate(false); void changeDate(value); }} />}
		</div>
	);
};

const DateForm = styled.div`
	display: grid;
	gap: 16px;
	.buttons {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
	}
`;

/** The picker reads and returns UTC days, the same "YYYY-MM-DD" the server stores. */
const ActivationDateDialog = ({ initial, onClose, onSave }: { initial: unknown; onClose: () => void; onSave: (value: string) => void }) => {
	useLocale();
	const [selected, setSelected] = useState<Date>(() => new Date(String(initial)));
	return (
		<ProviderDialog width={440} eyebrow={msg("feeConfiguration")} title={msg("changeActivationDate")} onClose={onClose}>
			<DialogBody>
				<Card>
					<div className="card-body">
						<DateForm>
							<DatePickerField label={msg("appliesFrom")} value={toUTCISODate(selected)} onChange={value => setSelected(new Date(value))} />
							<div className="buttons">
								<AdminButton variant="secondarySmall" size="small" onClick={onClose}>{msg("cancel")}</AdminButton>
								<AdminButton variant="primarySmall" size="small" onClick={() => onSave(selected.toISOString().split("T")[0])}>{msg("saveDate")}</AdminButton>
							</div>
						</DateForm>
					</div>
				</Card>
			</DialogBody>
		</ProviderDialog>
	);
};

export const S_ADD_FEES_MODERN = new Signal<{ target_id: number; type: TFeesType; target_type: TFeesTargetType; label?: string; onClose: () => void } | null>(
	"S_ADD_FEES_MODERN",
);

/** The new-style twin of AddFeesPopup: the server drafts a configuration, the user sets its date and values. */
export const AddFeesDialog = () => {
	useLocale();
	const request = S_ADD_FEES_MODERN.use();
	const [generated, setGenerated] = useState<IAPIResponse | null>(null);
	const [busy, setBusy] = useState(false);

	useEffect(() => {
		if (!request) return;
		let active = true;
		setGenerated(null);
		setBusy(true);
		void GD.REQ_API.request({
			method: "fees/generate",
			data: { target_id: request.target_id, target_type: request.target_type, type: request.type, ignore_fees_copy: true },
		}).then(response => {
			if (!active) return;
			setBusy(false);
			setGenerated(response);
		});
		return () => {
			active = false;
		};
	}, [request]);

	if (!request) return null;

	const close = () => {
		setGenerated(null);
		setBusy(false);
		S_ADD_FEES_MODERN.invoke(null);
		request.onClose();
	};
	const text = TEXT[request.type];

	return (
		<ProviderDialog width={900} fullHeight eyebrow={request.label ? msg("templateNew_322c3120", { v0: text.one, v1: request.label }) : msg("templateNew_077d6105", { v0: text.one })} title={msg("templateAdd_b69dceff", { v0: text.title.toLowerCase() })} busy={busy} onClose={close}>
			<DialogBody>
				{generated?.error && <Notice tone="error">{generated.error.message}</Notice>}
				{!generated && <Card><SkeletonRows rows={3} /></Card>}
				{generated && !generated.error && (
					<>
						<Section title={msg("whenItApplies")} />
						<FormParser
							appearance="modern"
							sx={embeddedFormSx}
							size="small"
							primaryButton={null}
							secondaryButton={null}
							onChange={(data: any) => {
								generated.data.activated_from = data.setup.activated_from;
							}}
							onRequest={async () => ({
								setup: {
									activated_from: generated.data.activated_from,
									["$__activated_from_type"]: "date",
									currency: generated.data.currency,
									["$__currency_disabled"]: true,
								},
								["$__setup_decorator"]: {
									blocks: [{ order: ["activated_from", "currency"], gridTemplateColumns: "180px 120px" }],
								},
							})}
							onSubmit={async () => {}}
						/>
						<Section title={msg("values")} />
						<FormParser
							appearance="modern"
							sx={embeddedFormSx}
							size="small"
							key={"fees_add" + request.target_id + request.type + request.target_type + generated.data.id}
							secondaryButton={{ title: msg("cancel"), onClick: close }}
							primaryButton={{ title: msg("addConfiguration") }}
							onSubmit={async (data: any): Promise<any> => {
								const response = await GD.REQ_API.request({ method: "fees/set", data });
								if (response.error) return response.error;
								notify.success(msg("templateConfigurationAdded_01a3fe8f", { v0: text.title }));
								close();
								return response;
							}}
							onRequest={async () => generated}
						/>
					</>
				)}
			</DialogBody>
		</ProviderDialog>
	);
};

